import { hasValue } from '@repo/utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';

import type { TrajectoryContext, TrajectorySelection, TrajectoryWellboreRef } from '../types/data';
import type { TGraphTrajectoryView } from '../types/graph-trajectory';
import type { ReadonlyDeep } from '../types/readonly-deep';
import {
  copyTrajectorySelectedReferenceTree,
  EMPTY_TRAJECTORY_SELECTED_REFERENCE_TREE,
  type TTrajectorySelectedReferenceTree,
} from '../types/selected-reference-data';
import type { TTrajectory } from '../types/trajectory';
import { OperationAbortedError } from '../utils/errors';
import { getTrajectoryLineColor } from '../utils/trajectory-line-colors';

import type { TrajectoriesApi } from './api/trajectories-api';
import { collectFactTrajectoryRequests, type TWellboreTrajectoryRequest } from './collect-fact-trajectory-requests';
import type { FactTrajectoryEntity } from './entities';
import { mapApiTrajectoryToFactTrajectory, mapFactTrajectory } from './mappers';
import type { TrajectoryPlanOrFact } from './model';

type TInternalWellboreRequest = {
  wellboreId: number;
  trajectoryIds: Set<number>;
};

export class TrajectoryService {
  @observable.ref private _wellId: number | null = null;
  @observable.ref private _planOrFact: TrajectoryPlanOrFact | null = null;
  @observable.ref private _wellbores: ReadonlyDeep<TrajectoryWellboreRef[]> | null = null;
  @observable.ref private _selectedWellboreId: number | null = null;
  @observable.ref private _selectedTrajectoryId: number | null = null;
  @observable.ref private _selectedReferenceData: TTrajectorySelectedReferenceTree =
    copyTrajectorySelectedReferenceTree(EMPTY_TRAJECTORY_SELECTED_REFERENCE_TREE);

  @observable.ref private _mainTrajectory: TGraphTrajectoryView | null = null;
  @observable.ref private _referenceTrajectories: TGraphTrajectoryView[] = [];
  /** Full trajectory list for the selected wellbore (sidebar / metadata). */
  @observable.ref private _wellboreTrajectories: TTrajectory[] = [];
  @observable private _isWellboreTrajectoriesLoading = false;

  private readonly api: TrajectoriesApi;
  private readonly cache = new Map<number, TTrajectory[]>();
  private abortController: AbortController | null = null;
  private listAbortController: AbortController | null = null;
  private loadGeneration = 0;

  constructor(api: TrajectoriesApi) {
    this.api = api;
    makeObservable(this);
  }

  @computed
  get wellId(): number | null {
    return this._wellId;
  }

  @computed
  get wellbores(): ReadonlyDeep<TrajectoryWellboreRef[]> | null {
    return this._wellbores;
  }

  @computed
  get selectedWellboreId(): number | null {
    return this._selectedWellboreId;
  }

  @computed
  get selectedTrajectoryId(): number | null {
    return this._selectedTrajectoryId;
  }

  @computed
  get mainTrajectory(): TGraphTrajectoryView | null {
    return this._mainTrajectory;
  }

  @computed
  get mainFactTrajectory(): FactTrajectoryEntity | null {
    return this._mainTrajectory?.entity ?? null;
  }

  @computed
  get graphTrajectories(): TGraphTrajectoryView[] {
    if (!this._mainTrajectory) {
      return this._referenceTrajectories;
    }

    return [this._mainTrajectory, ...this._referenceTrajectories];
  }

  @computed
  get selectedReferenceData(): TTrajectorySelectedReferenceTree {
    return this._selectedReferenceData;
  }

  @computed
  get hasWell(): boolean {
    return hasValue(this._wellId);
  }

  @computed
  get hasWellboreSelection(): boolean {
    return hasValue(this._selectedWellboreId);
  }

  @computed
  get hasTrajectorySelection(): boolean {
    return hasValue(this._selectedTrajectoryId);
  }

  @computed
  get wellboreTrajectories(): ReadonlyDeep<TTrajectory[]> {
    return this._wellboreTrajectories;
  }

  @computed
  get isWellboreTrajectoriesLoading(): boolean {
    return this._isWellboreTrajectoriesLoading;
  }

  @action.bound
  setContext(context: TrajectoryContext): void {
    this._wellId = context.wellId;

    if ('planOrFact' in context) {
      this._planOrFact = context.planOrFact ?? null;
    }

    void this.syncLoadedTrajectories();
  }

  @action.bound
  setWellbores(wellbores: ReadonlyDeep<TrajectoryWellboreRef[]> | null): void {
    this._wellbores = wellbores;
  }

  @action.bound
  setSelection(selection: TrajectorySelection): void {
    if ('wellboreId' in selection) {
      const nextWellboreId = selection.wellboreId ?? null;

      if (this._selectedWellboreId !== nextWellboreId) {
        this._selectedWellboreId = nextWellboreId;
        this._wellboreTrajectories = hasValue(nextWellboreId) ? (this.cache.get(nextWellboreId) ?? []) : [];
      }
    }

    if ('trajectoryId' in selection) {
      this._selectedTrajectoryId = selection.trajectoryId ?? null;
    }

    void this.syncLoadedTrajectories();
  }

  @action.bound
  onSelectedReferenceDataChange(data: TTrajectorySelectedReferenceTree): void {
    this._selectedReferenceData = copyTrajectorySelectedReferenceTree(data);
    void this.syncLoadedTrajectories();
  }

  /** Loads (or reads from cache) all trajectories for the selected wellbore. */
  @flow.bound
  async *ensureWellboreTrajectoriesLoaded() {
    const wellboreId = this._selectedWellboreId;

    this.listAbortController?.abort();
    this.listAbortController = null;

    if (!hasValue(wellboreId)) {
      this._wellboreTrajectories = [];
      this._isWellboreTrajectoriesLoading = false;
      return;
    }

    const cached = this.cache.get(wellboreId);

    if (cached) {
      this._wellboreTrajectories = cached;
      this._isWellboreTrajectoriesLoading = false;
      return;
    }

    const abortController = new AbortController();
    this.listAbortController = abortController;
    this._isWellboreTrajectoriesLoading = true;

    try {
      const trajectories: TTrajectory[] = yield this.api.fetchByWellbore(wellboreId, abortController.signal);

      if (this.listAbortController !== abortController) {
        return;
      }

      this.cache.set(wellboreId, trajectories);
      this._wellboreTrajectories = trajectories;
    } catch (e) {
      if (this.listAbortController !== abortController || e instanceof OperationAbortedError) {
        return;
      }

      this._wellboreTrajectories = [];
    } finally {
      if (this.listAbortController === abortController) {
        this._isWellboreTrajectoriesLoading = false;
        this.listAbortController = null;
      }
    }
  }

  getTrajectoryById(trajectoryId: number | null | undefined): ReadonlyDeep<TTrajectory> | null {
    if (!hasValue(trajectoryId)) {
      return null;
    }

    return this._wellboreTrajectories.find((item) => item.id === trajectoryId) ?? null;
  }

  @flow.bound
  private async *syncLoadedTrajectories() {
    this.abortController?.abort();
    this.abortController = null;

    const generation = ++this.loadGeneration;

    if (this._planOrFact === 'plan') {
      this.clearVisualTrajectories();
      return;
    }

    const collected: TWellboreTrajectoryRequest[] = collectFactTrajectoryRequests({
      planOrFact: this._planOrFact,
      selectedWellboreId: this._selectedWellboreId,
      selectedTrajectoryId: this._selectedTrajectoryId,
      selectedReferenceData: this._selectedReferenceData,
    });

    const requests: TInternalWellboreRequest[] = collected.map((item) => ({
      wellboreId: item.wellboreId,
      trajectoryIds: new Set(item.trajectoryIds),
    }));

    if (requests.length === 0) {
      this.clearVisualTrajectories();
      return;
    }

    const missingWellboreIds = requests.map((item) => item.wellboreId).filter((id) => !this.cache.has(id));

    const abortController = new AbortController();
    this.abortController = abortController;

    try {
      if (missingWellboreIds.length > 0) {
        const results: PromiseSettledResult<{ wellboreId: number; trajectories: TTrajectory[] }>[] =
          yield Promise.allSettled(
            missingWellboreIds.map(async (wellboreId) => {
              const trajectories = await this.api.fetchByWellbore(wellboreId, abortController.signal);
              return { wellboreId, trajectories };
            }),
          );

        if (generation !== this.loadGeneration) {
          return;
        }

        for (const result of results) {
          if (result.status === 'fulfilled') {
            this.cache.set(result.value.wellboreId, result.value.trajectories);

            if (result.value.wellboreId === this._selectedWellboreId) {
              this._wellboreTrajectories = result.value.trajectories;
            }
          }
        }
      }

      if (generation !== this.loadGeneration) {
        return;
      }

      this.applyLoadedTrajectories(requests);
    } catch (e) {
      if (generation !== this.loadGeneration || e instanceof OperationAbortedError) {
        return;
      }

      this.applyLoadedTrajectories(requests);
    } finally {
      if (generation === this.loadGeneration && this.abortController === abortController) {
        this.abortController = null;
      }
    }
  }

  @action.bound
  private applyLoadedTrajectories(requests: TInternalWellboreRequest[]): void {
    const mainId = this._selectedTrajectoryId;
    const mainWellboreId = this._selectedWellboreId;
    let main: TGraphTrajectoryView | null = null;
    const references: TGraphTrajectoryView[] = [];
    const seenIds = new Set<number>();

    const toView = (raw: TTrajectory, role: TGraphTrajectoryView['role']): TGraphTrajectoryView => {
      const fact = mapApiTrajectoryToFactTrajectory(raw, this._wellId);

      return {
        id: fact.id,
        title: fact.title,
        wellboreId: fact.wellboreId,
        role,
        color: getTrajectoryLineColor(fact.id),
        entity: mapFactTrajectory(fact),
      };
    };

    for (const request of requests) {
      const cached = this.cache.get(request.wellboreId) ?? [];

      for (const raw of cached) {
        if (!request.trajectoryIds.has(raw.id) || seenIds.has(raw.id)) {
          continue;
        }

        seenIds.add(raw.id);

        const isMain =
          hasValue(mainId) && hasValue(mainWellboreId) && raw.id === mainId && request.wellboreId === mainWellboreId;
        const view = toView(raw, isMain ? 'main' : 'reference');

        if (view.role === 'main') {
          main = view;
        } else {
          references.push(view);
        }
      }
    }

    this._mainTrajectory = main;
    this._referenceTrajectories = references;
  }

  @action.bound
  private clearVisualTrajectories(): void {
    this._mainTrajectory = null;
    this._referenceTrajectories = [];
  }

  @action.bound
  dispose(): void {
    this.abortController?.abort();
    this.abortController = null;
    this.listAbortController?.abort();
    this.listAbortController = null;
    this.loadGeneration += 1;
    this.cache.clear();
    this._wellId = null;
    this._planOrFact = null;
    this._wellbores = null;
    this._selectedWellboreId = null;
    this._selectedTrajectoryId = null;
    this._selectedReferenceData = copyTrajectorySelectedReferenceTree(EMPTY_TRAJECTORY_SELECTED_REFERENCE_TREE);
    this._wellboreTrajectories = [];
    this._isWellboreTrajectoriesLoading = false;
    this.clearVisualTrajectories();
  }
}
